import { useState } from "react";
import { useTranslation } from "react-i18next";
import { portfolioCases, categories, methodFilters } from "@/mocks/portfolio";
import PortfolioDetailModal from "./PortfolioDetailModal";
import type { PortfolioCase } from "@/mocks/portfolio";

export default function CasesSection() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeMethod, setActiveMethod] = useState<string>("all");
  const [selectedCase, setSelectedCase] = useState<PortfolioCase | null>(null);

  const filteredCases = portfolioCases.filter((c) => {
    const categoryMatch = activeFilter === "all" || c.tagKey === activeFilter;
    const methodMatch = activeMethod === "all" || c.method === activeMethod;
    return categoryMatch && methodMatch;
  });

  return (
    <section className="section-padding bg-cream">
      <div className="container-main">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <span className="tag-label mb-4 inline-block">{t("portfolio.filter.label")}</span>
          <h2 className="mb-4">{t("portfolio.filter.title")}</h2>
          <p className="text-medium-gray text-[1.0625rem]">{t("portfolio.filter.subtitle")}</p>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2.5 mb-6">
          {categories.map((cat) => {
            const isActive = activeFilter === cat.value;
            return (
              <button
                key={cat.value}
                onClick={() => setActiveFilter(cat.value)}
                className={`px-4 py-2.5 rounded-full text-[1rem] font-sans font-medium whitespace-nowrap cursor-pointer transition-all duration-300 ${
                  isActive
                    ? "bg-graphite text-cream"
                    : "bg-white text-graphite border border-warm-gray/60 hover:border-champagne hover:text-champagne"
                }`}
              >
                {t(cat.key)}
              </button>
            );
          })}
        </div>

        {/* Method Filter Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10 md:mb-14">
          <span className="text-sm text-medium-gray self-center mr-1">工法：</span>
          {methodFilters.map((m) => {
            const isActive = activeMethod === m.value;
            return (
              <button
                key={m.value}
                onClick={() => setActiveMethod(m.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  isActive
                    ? "bg-champagne text-graphite shadow-sm"
                    : "bg-white text-medium-gray border border-warm-gray/40 hover:border-champagne/40 hover:text-graphite"
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Results Count */}
        <p className="text-center text-sm text-medium-gray mb-6">
          共 {filteredCases.length} 個案例
          {activeMethod !== "all" && (
            <span className="ml-1">
              （{methodFilters.find((m) => m.value === activeMethod)?.label}）
            </span>
          )}
        </p>

        {/* Case Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filteredCases.map((item) => (
            <div
              key={item.id}
              className="group bg-white rounded-xl overflow-hidden border border-warm-gray/60 transition-all duration-300 hover:translate-y-[-6px] cursor-pointer"
              onClick={() => setSelectedCase(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setSelectedCase(item);
              }}
            >
              {/* Image */}
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-graphite/40 to-transparent" />
                <span className="absolute bottom-4 left-4 tag-label">{item.tag}</span>

                {/* Method badge */}
                {item.method && (
                  <span className="absolute top-4 left-4 tag-label bg-white/90 text-graphite">
                    {methodFilters.find((m) => m.value === item.method)?.label ?? item.method}
                  </span>
                )}

                {/* View detail hint */}
                <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-graphite opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-eye-line" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6 md:p-7">
                <h3 className="text-[1.25rem] md:text-[1.375rem] mb-3 leading-snug">
                  {item.title}
                </h3>
                <p className="text-medium-gray text-[1rem] leading-relaxed mb-5">
                  {item.desc}
                </p>

                {/* Points */}
                <div className="space-y-2 mb-6">
                  {item.points.map((point, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-champagne/10 shrink-0">
                        <i className="ri-check-line text-base text-champagne" />
                      </span>
                      <span className="text-graphite text-[1rem] font-sans font-medium">{point}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <a
                  href="https://lin.ee/OpssTIM"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline w-full justify-center text-base"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-line-fill" />
                  </span>
                  {t("portfolio.card.cta")}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {filteredCases.length === 0 && (
          <div className="text-center py-16">
            <span className="w-16 h-16 rounded-full bg-cream flex items-center justify-center text-champagne mx-auto mb-4">
              <i className="ri-search-line text-2xl" />
            </span>
            <p className="text-medium-gray text-base mb-2">沒有符合此條件的案例</p>
            <p className="text-medium-gray/70 text-sm mb-4">試試其他工法或情境分類</p>
            <button
              onClick={() => { setActiveFilter("all"); setActiveMethod("all"); }}
              className="btn-secondary cursor-pointer"
            >
              顯示所有案例
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <PortfolioDetailModal item={selectedCase} onClose={() => setSelectedCase(null)} />
    </section>
  );
}